'use client';
import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { SlidersHorizontal } from 'lucide-react';
import { Portal } from '@radix-ui/react-portal';

// GraphQL imports
import { FeedItemType, RecCategory, FeedItemOrderBy } from 'shared/__generated__/graphql';

// Components
import { Button, TWButton } from '../../button';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '../../ui/dropdown-menu';
import { Drawer, DrawerTrigger, DrawerContent } from '../../ui/drawer';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';

// Hooks and utilities
import { cn } from '../../../utils/cn';
import {
  REC_CATEGORY,
  getRecQueryCategoryAsEmoji,
  getRecQueryCategoryAtString,
} from 'shared/data/rec';

import {
  useFeedToolbarUtils,
  UseFeedToolbarUtilProps,
  FEED_ITEM_ORDER_BY_LABELS,
  ENABLED_FEED_ITEM_TYPES_FOR_CATEGORY_FILTER,
} from 'shared/misc/components/by-type/feed/utils/useFeedToolbarUtils';
import { useBreakpoint } from 'shared/misc/providers/breakpoint-provider';
import { useRouterState } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';

// Helper function to make feed item types human readable
const getFeedItemTypeLabel = (type: FeedItemType): string => {
  const labels: Partial<Record<FeedItemType, string>> = {
    [FeedItemType.AddRecommendation]: 'Recommendations',
    [FeedItemType.NewPrompt]: 'Asks',
    [FeedItemType.NewRePost]: 'Reposts',
    [FeedItemType.NewRePostWithContent]: 'Reposts with Content',
    [FeedItemType.NewPromptRecReply]: 'Ask Replies',
    [FeedItemType.NewComment]: 'Comments',
    [FeedItemType.NewUserFollow]: 'Follows',
  };
  return labels[type] || type.replace(/([A-Z])/g, ' $1').trim();
};

type GenericFeedToolbarProps = UseFeedToolbarUtilProps & {
  promptCount?: number;
  recCount?: number;
};

export function useGenericFeedToolbar(props: GenericFeedToolbarProps) {
  const breakpoint = useBreakpoint();
  const isMobile = ['sm', 'base'].includes(breakpoint);

  const router = useRouter();

  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const initialIsInline = useMemo(() => {
    if (typeof window !== 'undefined') {
      const savedState = localStorage?.getItem('isInline');
      return savedState ? JSON.parse(savedState) : false;
    }
    return false;
  }, []);

  const {
    control,
    values,
    setValue,
    variables,
    isNonDefaultFilter,
    isInline,
    isIncompatibleFeedType,
    toggleOnlyAsks,
    toggleOnlyRecs,
    toggleAll,
    toggleInlineView,
    isOnlyRecs,
    isOnlyAsks,
    isNonDefaultSortBy,
  } = useFeedToolbarUtils({
    ...props,
    initialIsInline,
    pathname,
    navigate: (...args) => router.navigate(...args),
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage?.setItem('isInline', JSON.stringify(isInline));
    }
  }, [isInline]);

  const sortByAsInlineMenuNode = (
    <Controller
      control={control}
      name="orderBy"
      render={({ field: { onChange, value } }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              className={cn(
                'text-brand-main bg-brand-background border border-solid border-brand-main hover:border-brand-highlight focus:border-brand-highlight focus:outline-none hover:border-solid focus:box-shadow-none flex items-center gap-2 typography-heading-sm h-[38px]',
                isNonDefaultSortBy
                  ? 'bg-brand-main text-brand-background'
                  : 'bg-brand-background text-brand-main',
              )}
            >
              <div className="typography-action-sm">
                {FEED_ITEM_ORDER_BY_LABELS[value || FeedItemOrderBy.NewestFirst]}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-brand-background text-brand-main mt-[12px] w-[200px]">
            {Object.entries(FEED_ITEM_ORDER_BY_LABELS)
              .filter(
                ([key]) => props.enabledOrderByOptions?.includes(key as FeedItemOrderBy) || true,
              )
              .map(([orderByValue, label]) => {
                const typedOrderByValue = orderByValue as FeedItemOrderBy;
                return (
                  <DropdownMenuItem
                    key={orderByValue}
                    className="typography-action-sm"
                    onSelect={() => {
                      onChange(typedOrderByValue);
                      if (isIncompatibleFeedType) {
                        setValue('feedItemTypes', ENABLED_FEED_ITEM_TYPES_FOR_CATEGORY_FILTER);
                      }
                    }}
                  >
                    {label}
                  </DropdownMenuItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    />
  );

  const viewModeToggleNode = (
    <div className="flex gap-4">
      <div className="before:absolute before:inset-0 before:bg-[var(--brand-main)] before:opacity-20 before:-z-10 border border-solid border-brand-main self-center h-[38px] m-0 w-[115px] relative">
        <div className="absolute transition-all duration-300 ease-in-out w-[115px] flex justify-between left-[3px]">
          <Button
            className={cn(
              'w-[85px] mt-[3px] h-[30px] border-brand-main border-1 border-solid border-[1px] whitespace-nowrap',
              'bg-brand-background focus:outline-none focus:box-shadow-none self-center',
              'hover:border-brand-highlight hover:text-brand-highlight flex items-center justify-center self-center typography-heading-sm',
            )}
            onClick={toggleInlineView}
            variant="secondaryBlack"
          >
            <div className="text-brand-main bg-brand-background typography-action-sm uppercase">
              {isInline ? '🤏 Compact' : '👀 Full'}
            </div>
          </Button>
        </div>
      </div>
      {sortByAsInlineMenuNode}
    </div>
  );

  const feedTypesAsInlineMenuNode = (
    <Controller
      control={control}
      name="feedItemTypes"
      render={({ field: { ref: _ref, onChange, value } }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="text-brand-main bg-brand-background typography-heading-sm border border-solid border-brand-main hover:border-brand-highlight focus:border-brand-highlight focus:outline-none hover:border-solid focus:box-shadow-none min-w-[129px]">
              {value.length > 0 ? `${value.length} Selected` : 'Default Types'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-brand-background text-brand-main">
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onChange([]);
              }}
            >
              <input type="checkbox" checked={value.length === 0} readOnly />
              Default Types
            </DropdownMenuItem>
            {[
              FeedItemType.AddRecommendation,
              FeedItemType.NewPrompt,
              FeedItemType.NewRePost,
              FeedItemType.NewRePostWithContent,
              FeedItemType.NewPromptRecReply,
              FeedItemType.NewComment,
              FeedItemType.NewUserFollow,
            ].map((type) => (
              <DropdownMenuItem
                className="w-[200px] text-brand-main"
                key={type}
                onSelect={(e) => {
                  e.preventDefault();
                  const newValue = value.includes(type)
                    ? value.filter((t) => t !== type)
                    : [...value, type];
                  onChange(newValue);
                }}
              >
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={value.includes(type)} readOnly />
                  {getFeedItemTypeLabel(type)}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    />
  );

  const categoryAsInlineMenuNode = (
    <Controller
      control={control}
      name="category"
      render={({ field: { ref: _ref, onChange, value } }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="text-brand-main bg-brand-background typography-heading-sm border border-solid border-brand-main hover:border-brand-highlight focus:border-brand-highlight focus:outline-none hover:border-solid focus:box-shadow-none min-w-[129px]">
              {value
                ? `${getRecQueryCategoryAsEmoji(value)} ${getRecQueryCategoryAtString(value)}`
                : 'All Categories'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-brand-background text-brand-main">
            <DropdownMenuItem onSelect={() => onChange(null)}>⭐ Everything</DropdownMenuItem>
            {[
              REC_CATEGORY.MUSIC,
              REC_CATEGORY.MOVIES,
              REC_CATEGORY.BOOKS,
              REC_CATEGORY.CLOTHING,
              REC_CATEGORY.FOOD_AND_DRINK,
              REC_CATEGORY.TV,
              REC_CATEGORY.VIDEOS,
              REC_CATEGORY.PLACES,
              REC_CATEGORY.MISC,
            ].map((categoryOption: REC_CATEGORY) => {
              const title = getRecQueryCategoryAtString(categoryOption);
              const emoji = getRecQueryCategoryAsEmoji(categoryOption);
              return (
                <DropdownMenuItem
                  className="w-[200px] text-brand-main"
                  key={categoryOption}
                  onSelect={() => onChange(categoryOption)}
                >
                  {emoji} {title}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    />
  );

  const allFilterNodes = (
    <>
      {sortByAsInlineMenuNode}
      {feedTypesAsInlineMenuNode}
      {categoryAsInlineMenuNode}
    </>
  );

  const quickFilterNodes = (
    <>
      <TWButton
        variant="secondary"
        className={cn(
          'text-brand-main bg-brand-background border border-solid border-brand-main hover:border-brand-highlight focus:border-brand-highlight focus:outline-none hover:border-solid focus:box-shadow-none flex items-center gap-2 typography-heading-sm h-[38px]',
          !isNonDefaultFilter
            ? 'bg-brand-main text-brand-background'
            : 'bg-brand-background text-brand-main',
        )}
        onClick={toggleAll}
      >
        All
      </TWButton>
      <TWButton
        variant="secondary"
        className={cn(
          'text-brand-main bg-brand-background border border-solid border-brand-main hover:border-brand-highlight focus:border-brand-highlight focus:outline-none hover:border-solid focus:box-shadow-none flex items-center gap-2 typography-heading-sm h-[38px]',
          isOnlyRecs
            ? 'bg-brand-main text-brand-background'
            : 'bg-brand-background text-brand-main',
        )}
        onClick={toggleOnlyRecs}
      >
        Recs{props.recCount ? ` (${props.recCount})` : ''}
      </TWButton>
      <TWButton
        variant="secondary"
        className={cn(
          'text-brand-main bg-brand-background border border-solid border-brand-main hover:border-brand-highlight focus:border-brand-highlight focus:outline-none hover:border-solid focus:box-shadow-none flex items-center gap-2 typography-heading-sm h-[38px]',
          isOnlyAsks
            ? 'bg-brand-main text-brand-background'
            : 'bg-brand-background text-brand-main',
        )}
        onClick={toggleOnlyAsks}
      >
        Asks{props.promptCount ? ` (${props.promptCount})` : ''}
      </TWButton>
    </>
  );

  const feedFilterContentNode = (
    <div className={cn('flex flex-col gap-6')}>
      <div className="flex flex-col gap-4">
        {isMobile && (
          <div>
            <h2 className="typography-heading-sm my-4 text-brand-main">SORT</h2>
            <div className="flex flex-wrap gap-2">
              <Controller
                control={control}
                name="orderBy"
                render={({ field: { onChange, value } }) => (
                  <>
                    <button
                      onClick={() => onChange(FeedItemOrderBy.NewestFirst)}
                      className={cn(
                        'px-3 py-1 border border-brand-main rounded-none',
                        'hover:border-brand-highlight focus:border-brand-highlight',
                        'typography-action-sm',
                        value === FeedItemOrderBy.NewestFirst
                          ? 'bg-brand-main text-brand-background'
                          : 'bg-brand-background text-brand-main',
                      )}
                    >
                      {FEED_ITEM_ORDER_BY_LABELS[FeedItemOrderBy.NewestFirst]}
                    </button>
                    {Object.entries(FEED_ITEM_ORDER_BY_LABELS)
                      .filter(
                        ([key]) =>
                          key !== FeedItemOrderBy.NewestFirst &&
                          (props.enabledOrderByOptions?.includes(key as FeedItemOrderBy) || true),
                      )
                      .map(([orderByValue, label]) => {
                        const typedOrderByValue = orderByValue as FeedItemOrderBy;
                        return (
                          <button
                            key={orderByValue}
                            onClick={() => onChange(typedOrderByValue)}
                            className={cn(
                              'px-3 py-1 border border-brand-main rounded-none',
                              'hover:border-brand-highlight focus:border-brand-highlight',
                              'typography-action-sm',
                              value === typedOrderByValue
                                ? 'bg-brand-main text-brand-background'
                                : 'bg-brand-background text-brand-main',
                            )}
                          >
                            {label}
                          </button>
                        );
                      })}
                  </>
                )}
              />
            </div>
          </div>
        )}

        <div>
          <h2 className="typography-heading-sm mb-4 text-brand-main">ACTIVITY</h2>
          <div className="flex flex-wrap gap-2">
            <Controller
              control={control}
              name="feedItemTypes"
              render={({ field: { onChange, value } }) => (
                <>
                  <button
                    onClick={() => onChange([])}
                    className={cn(
                      'px-3 py-1 border border-brand-main rounded-none',
                      'hover:border-brand-highlight focus:border-brand-highlight',
                      'typography-action-sm',
                      value.length === 0
                        ? 'bg-brand-main text-brand-background'
                        : 'bg-brand-background text-brand-main',
                    )}
                  >
                    ⭐ All
                  </button>
                  {[
                    { type: FeedItemType.AddRecommendation, label: 'Recs' },
                    { type: FeedItemType.NewPrompt, label: 'Asks' },
                    { type: FeedItemType.NewRePost, label: 'Reposts' },
                    {
                      type: FeedItemType.NewRePostWithContent,
                      label: 'Reposts w/ Content',
                    },
                    { type: FeedItemType.NewPromptRecReply, label: 'Ask Replies' },
                    { type: FeedItemType.NewComment, label: 'Comments' },
                    { type: FeedItemType.NewUserFollow, label: 'Follows' },
                  ].map(({ type, label }) => (
                    <button
                      key={type}
                      onClick={() => {
                        const newValue = value.includes(type)
                          ? value.filter((t) => t !== type)
                          : [...value, type];
                        onChange(newValue);
                      }}
                      className={cn(
                        'px-3 py-1 border border-brand-main rounded-none',
                        'hover:border-brand-highlight focus:border-brand-highlight',
                        'typography-action-sm',
                        value.includes(type)
                          ? 'bg-brand-main text-brand-background'
                          : 'bg-brand-background text-brand-main',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </>
              )}
            />
          </div>
        </div>

        <div>
          <h2 className="typography-heading-sm mb-4 text-brand-main">CATEGORIES</h2>
          <div className="flex flex-wrap gap-2">
            <Controller
              control={control}
              name="category"
              render={({ field: { onChange, value } }) => (
                <>
                  <button
                    onClick={() => onChange(null)}
                    className={cn(
                      'px-3 py-1 border border-brand-main rounded-none',
                      'hover:border-brand-highlight focus:border-brand-highlight',
                      'typography-action-sm flex items-center gap-1',
                      !value
                        ? 'bg-brand-main text-brand-background'
                        : 'bg-brand-background text-brand-main',
                    )}
                  >
                    ⭐ All
                  </button>
                  {[
                    { emoji: '🎵', cat: RecCategory.Music, label: 'Music' },
                    { emoji: '🎬', cat: RecCategory.Movies, label: 'Movies' },
                    { emoji: '📚', cat: RecCategory.Books, label: 'Books' },
                    {
                      emoji: '🍽️',
                      cat: RecCategory.FoodAndDrink,
                      label: 'Food & Drink',
                    },
                    { emoji: '👕', cat: RecCategory.Clothing, label: 'Clothes' },
                    { emoji: '📹', cat: RecCategory.Videos, label: 'Videos' },
                    { emoji: '📍', cat: RecCategory.Places, label: 'Places' },
                    { emoji: '🧠', cat: RecCategory.Misc, label: 'Misc' },
                  ].map(({ emoji, cat, label }) => (
                    <button
                      key={cat}
                      onClick={() => {
                        onChange(cat);
                        if (isIncompatibleFeedType) {
                          setValue('feedItemTypes', ENABLED_FEED_ITEM_TYPES_FOR_CATEGORY_FILTER);
                        }
                      }}
                      className={cn(
                        'px-3 py-1 border border-brand-main rounded-none',
                        'hover:border-brand-highlight focus:border-brand-highlight',
                        'typography-action-sm flex items-center gap-1',
                        value === cat
                          ? 'bg-brand-main text-brand-background'
                          : 'bg-brand-background text-brand-main',
                      )}
                    >
                      {emoji} {label}
                    </button>
                  ))}
                </>
              )}
            />
          </div>
        </div>
      </div>

      <button
        onClick={() => {
          setValue('category', null);
          setValue('feedItemTypes', []);
          setValue('orderBy', FeedItemOrderBy.NewestFirst);
        }}
        className="w-full border border-brand-main text-brand-main hover:border-brand-highlight hover:text-brand-highlight py-2 typography-action-sm"
      >
        CLEAR FILTERS
      </button>
    </div>
  );

  const drawerNode = (
    <div>
      <Drawer direction="bottom">
        <DrawerTrigger asChild>
          <Button
            variant="secondary"
            className={cn(
              'text-brand-main bg-brand-background border border-solid border-brand-main hover:border-brand-highlight focus:border-brand-highlight focus:outline-none hover:border-solid focus:box-shadow-none flex items-center gap-2 typography-heading-sm h-[38px]',
              isNonDefaultFilter || isNonDefaultSortBy
                ? 'bg-brand-main text-brand-background'
                : 'bg-brand-background text-brand-main',
            )}
          >
            <SlidersHorizontal size={16} />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="p-[12px] pt-[24px] flex h-[500px] flex-col border bg-brand-background bottom-2 fixed z-[500] outline-none w-full">
          <div className="mx-auto w-full max-w-sm">{feedFilterContentNode}</div>
        </DrawerContent>
      </Drawer>
    </div>
  );

  const popoverFilterNode = (
    <Popover>
      <PopoverTrigger asChild>
        <TWButton
          variant="secondary"
          className="text-brand-main bg-brand-background border border-solid border-brand-main hover:border-brand-highlight focus:border-brand-highlight focus:outline-none hover:border-solid focus:box-shadow-none flex items-center gap-2 typography-heading-sm h-[38px]"
        >
          <SlidersHorizontal size={16} />
        </TWButton>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          className="w-[300px] p-4 mt-[12px] bg-brand-background border border-brand-main"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
            }
          }}
        >
          {feedFilterContentNode}
        </PopoverContent>
      </Portal>
    </Popover>
  );

  const filterNode = isMobile ? (
    drawerNode
  ) : (
    <>
      {/* {sortByAsInlineMenuNode} */}
      {popoverFilterNode}
    </>
  );

  return {
    variables,
    isInline,
    quickFilterNodes,
    viewModeToggleNode,
    allFilterNodes,
    drawerNode,
    filterNode,
    sortByAsInlineMenuNode,
    feedTypesAsInlineMenuNode,
    categoryAsInlineMenuNode,
    isNonDefaultFilter,
    isMobile,
  };
}
